/**
 * assessmentModules.js
 *
 * Centralised registry of assessment pathway modules. Every piece of
 * infrastructure that needs to resolve a Firestore collection segment, decide
 * whether a module uses modular (subcollection) storage, or look up a
 * human-readable label should go through this registry instead of hardcoding
 * module-specific strings.
 *
 * HOW TO ADD A NEW MODULE
 * -----------------------
 * 1. Add an entry to ASSESSMENT_MODULES below.
 * 2. Run the org backfill script to add the module to the org's
 *    availableAssessmentModules list.
 * 3. The plumbing (contexts, path helpers, routing, storage predicates) will
 *    pick it up automatically because it reads from this registry.
 *
 * WHY THIS EXISTS
 * ---------------
 * Adult Autism was the first subcollection-based module and its code hardcoded
 * 'adult_autism' / 'adultAutism' throughout contexts, path helpers, storage
 * utils, routing, and rules. Adding CYP ADHD as a second modular module
 * exposed the need to parameterise those paths. This registry is the single
 * source of truth that prevents per-module copy-paste of plumbing code.
 */

/**
 * Registry of all assessment pathway modules.
 *
 * Each entry defines:
 *   - collection:       Firestore subcollection segment (snake_case)
 *   - assessmentDocId:  Document ID under the collection segment
 *   - label:            Human-readable label for UI display
 *   - modular:          Whether this module uses subcollection-based storage
 *                       (true) or the legacy monolithic patient doc (false)
 *   - activeAssignmentKey: Key under informant profile activeAssignments
 *                          (matches the collection segment)
 *   - hasPatientFormInstances: Whether questionnaires live on the patient
 *                          tree (Adult Autism self-report) or only on
 *                          informant trees (CYP ADHD — the child does not
 *                          complete forms)
 *   - hasInformantHistoryStage: Whether informants have an assessmentStages/history
 *                          prefill doc. CYP ADHD prefills CI from form answers
 *                          instead, so there is no history stage on the informant.
 */
export const ASSESSMENT_MODULES = Object.freeze({
  adultAdhd: {
    collection: null,
    assessmentDocId: null,
    label: 'Adult ADHD',
    modular: false,
    activeAssignmentKey: null,
    hasPatientFormInstances: false,
    hasInformantHistoryStage: false,
  },
  adultAutism: {
    collection: 'adult_autism',
    assessmentDocId: 'assessment_001',
    label: 'Adult Autism',
    modular: true,
    activeAssignmentKey: 'adult_autism',
    hasPatientFormInstances: true,
    hasInformantHistoryStage: true,
  },
  cypAdhd: {
    collection: 'cyp_adhd',
    assessmentDocId: 'assessment_001',
    label: 'CYP ADHD',
    modular: true,
    activeAssignmentKey: 'cyp_adhd',
    // All questionnaires live on the guardian / school-rep informant trees.
    // Querying patients/{id}/cyp_adhd/.../formInstances would be an empty
    // collection; without a matching rule it is also a permission-denied that
    // aborts the clinician overview load.
    hasPatientFormInstances: false,
    hasInformantHistoryStage: false,
  },
});

/**
 * Look up module config by key. Returns null for unknown modules, which
 * callers should treat as a safe no-op (prevents a typo from crashing
 * the whole app — log a warning and fall back to legacy behaviour).
 */
export function getModuleConfig(assessmentModule) {
  return ASSESSMENT_MODULES[assessmentModule] || null;
}

/**
 * Whether a given assessmentModule uses the subcollection-based data model.
 * Unknown modules return false (safe default — routes to legacy ADHD paths
 * which at least exist).
 */
export function isModularModule(assessmentModule) {
  return ASSESSMENT_MODULES[assessmentModule]?.modular === true;
}

/**
 * Resolve the Firestore collection segment for a modular module.
 * Returns null for non-modular (ADHD) or unknown modules.
 */
export function getModuleCollection(assessmentModule) {
  return ASSESSMENT_MODULES[assessmentModule]?.collection || null;
}

/**
 * Resolve the assessment document ID for a modular module.
 * Returns null for non-modular (ADHD) or unknown modules.
 */
export function getModuleAssessmentDocId(assessmentModule) {
  return ASSESSMENT_MODULES[assessmentModule]?.assessmentDocId || null;
}

/**
 * Resolve the activeAssignments sub-key used by informant profiles for
 * a given module. Returns null for non-modular modules.
 */
export function getModuleActiveAssignmentKey(assessmentModule) {
  return ASSESSMENT_MODULES[assessmentModule]?.activeAssignmentKey || null;
}

/**
 * Human-readable label for a module. Falls back to 'Adult ADHD' for unknown
 * modules (preserves legacy behaviour where missing assessmentModule meant ADHD).
 */
export function getModuleLabel(assessmentModule) {
  return ASSESSMENT_MODULES[assessmentModule]?.label || 'Adult ADHD';
}

/**
 * Whether questionnaires are stored on the patient tree for this module.
 * CYP ADHD returns false — forms live only on informant assignment trees.
 */
export function moduleHasPatientFormInstances(assessmentModule) {
  return ASSESSMENT_MODULES[assessmentModule]?.hasPatientFormInstances === true;
}

/**
 * Whether informants have an assessmentStages/history prefill document.
 * CYP ADHD returns false — history is captured as form answers, not a stage.
 */
export function moduleHasInformantHistoryStage(assessmentModule) {
  return ASSESSMENT_MODULES[assessmentModule]?.hasInformantHistoryStage === true;
}
